/**
 * Entity extraction for the OEM+ assistant — the nouns a question names.
 *
 * The regex engine this replaces read at most one machine id and one mine off
 * the lowercased text, and dropped everything else: a class ("surface
 * drills"), a component ("coupling"), a window ("next 7 days"), an amount
 * ("A$520"). The router scores intents against these, and the intents filter
 * by them.
 *
 * ## Rules that fix reported misroutes
 *
 * - Mine **codes** match case-sensitively on the raw text — `\b(KG|BR|MI|OH|
 *   GD|MR)\b` — so "Oh, what's…" is not Ochre Hill. Names and aliases
 *   (Kalbarri, Redgum, Spinifex, Western Tiers, …) match case-insensitively.
 * - Machine ids: every `[JLPTS]-NN` in the text, in order of appearance.
 * - Class words map to the register's `cls` values: "drills" is every drilling
 *   class, "surface" is `sdrill`, "loaders" is `lhd`, and so on.
 * - Deictic references ("this machine", "it", "this report") are flagged so
 *   the router can fill the entity from the page or the conversation.
 *
 * Engine layer — pure, synchronous, no data loads.
 */

import { FLEET, HEALTH, MINES, MINE_ORDER } from '../../data/core.ts';
import type { DivisionName, MachineClass, MineCode, Subclass } from '../../schema/machine.ts';
import { ALERTS } from '../seed/alerts.ts';
import { MODULES, type ModuleId } from '../seed/modules.ts';

/** Where an entity came from — the question, the page, or the last turn. */
export type EntitySource = 'text' | 'page' | 'memory';

/** A machine class filter, with the words the user used for it. */
export interface ClassFilter {
  readonly classes: readonly MachineClass[];
  /** Narrower than a class: "top-hammer" and "blasthole" are both `sdrill`. */
  readonly subclass?: Subclass;
  readonly division?: DivisionName;
  /** How to say it back: "surface drills", "loaders". */
  readonly label: string;
}

/** A component the question names, resolved against the condition register. */
export interface ComponentRef {
  /** The word used: "coupling", "rotary head". */
  readonly word: string;
  /** Matches `ComponentHealth.component` / `WorkOrder.component`. */
  readonly component: RegExp;
  /** `stock_check` keyword (matches part description/system). */
  readonly stock: string;
  /** Also matches work-order descriptions ("coupling" lives there, not in `component`). */
  readonly text: RegExp;
}

/** Everything the question names. */
export interface Entities {
  /** Machine ids in the text, upper-case, in order. */
  readonly machines: readonly string[];
  /** The focal machine — the first in the text, or filled from page/memory. */
  machine?: string;
  machineFrom?: EntitySource;
  readonly mines: readonly MineCode[];
  mine?: MineCode;
  mineFrom?: EntitySource;
  cls?: ClassFilter;
  readonly components: readonly ComponentRef[];
  readonly partNos: readonly string[];
  readonly docIds: readonly string[];
  readonly alertIds: readonly string[];
  readonly incidentIds: readonly string[];
  readonly woIds: readonly string[];
  /** An A$ amount, in dollars. */
  readonly amount?: number;
  /** Autonomy level L1–L4. */
  readonly level?: 1 | 2 | 3 | 4;
  /** A time window, in days, and how it was said. */
  days?: number;
  windowLabel?: string;
  module?: ModuleId;
  moduleFrom?: EntitySource;
  /** Deictic references. */
  readonly deictic: {
    /** "this machine", "this page", "this one". */
    readonly thisMachine: boolean;
    /** "it", "its", "that machine", "the same". */
    readonly it: boolean;
    readonly thisModule: boolean;
    readonly thisReport: boolean;
    /** "this site", "this mine", "here". */
    readonly thisSite: boolean;
  };
}

// ---------------------------------------------------------------------------
// Machines
// ---------------------------------------------------------------------------

const MACHINE_ID = /\b([JLPTS]-\d\d)\b/gi;
const KNOWN = new Set(FLEET.map((f) => f.machine_id));

export function machinesFromText(q: string): string[] {
  const out: string[] = [];
  for (const m of q.matchAll(MACHINE_ID)) {
    const id = m[1].toUpperCase();
    if (KNOWN.has(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Mines
// ---------------------------------------------------------------------------

/** Name and alias patterns, matched on the lowercased text. */
const MINE_NAMES: readonly (readonly [MineCode, RegExp])[] = [
  ['KG', /kalbarri/],
  ['BR', /broken ridge|cobar/],
  ['MI', /mount isolde|mt isolde|isolde|redgum/],
  ['OH', /ochre hill|ochre|barkly/],
  ['GD', /gawler deep|gawler|spinifex/],
  ['MR', /mount read|mt read|tasmania|\btas\b|western tiers/],
];

/** Codes only when written in capitals, on the raw text. */
const MINE_CODE = /\b(KG|BR|MI|OH|GD|MR)\b/g;

export function minesFromText(q: string): MineCode[] {
  const L = q.toLowerCase();
  const hits: { code: MineCode; at: number }[] = [];
  for (const [code, re] of MINE_NAMES) {
    const m = re.exec(L);
    if (m) hits.push({ code, at: m.index });
  }
  for (const m of q.matchAll(MINE_CODE)) {
    hits.push({ code: m[1] as MineCode, at: m.index ?? 0 });
  }
  hits.sort((a, b) => a.at - b.at);
  const out: MineCode[] = [];
  for (const h of hits) if (!out.includes(h.code)) out.push(h.code);
  return out;
}

/** "Kalbarri", "Broken Ridge" — a mine's short spoken name. */
export const mineShort = (m: MineCode): string =>
  MINES[m].name.replace(/ (Gold|Metals)$/, '');

// ---------------------------------------------------------------------------
// Classes
// ---------------------------------------------------------------------------

/** Ordered: the first match wins, so the narrow phrases sit above the broad. */
const CLASS_WORDS: readonly (readonly [RegExp, ClassFilter])[] = [
  [/load (&|and) haul|load-haul/, { classes: ['lhd', 'truck'], division: 'Load & Haul', label: 'Load & Haul' }],
  [/top[- ]?hammer/, { classes: ['sdrill'], subclass: 'Top-hammer drills', label: 'top-hammer drills' }],
  [/blast ?hole|\bdth\b drills?/, { classes: ['sdrill'], subclass: 'Blasthole drills', label: 'blasthole drills' }],
  [/surface( drill(s|ing|ers)?)?\b|open pit/, { classes: ['sdrill'], division: 'Surface Drilling', label: 'surface drills' }],
  [/underground drill(s|ing)?/, { classes: ['jumbo', 'longhole'], division: 'Underground Drilling', label: 'underground drills' }],
  [/jumbos?\b|development (drill|rig)s?/, { classes: ['jumbo'], subclass: 'Development drills', label: 'jumbos' }],
  [/longhole|production (drill|rig)s?/, { classes: ['longhole'], subclass: 'Production drills', label: 'production drills' }],
  [/\bdrills\b|\bdrill rigs?\b|drilling (fleet|machines)/, { classes: ['jumbo', 'longhole', 'sdrill'], label: 'drills' }],
  [/\blhds?\b|loaders?\b/, { classes: ['lhd'], subclass: 'Loaders', label: 'loaders' }],
  [/\btrucks?\b|haul trucks?|haulage/, { classes: ['truck'], subclass: 'Haulage trucks', label: 'trucks' }],
];

export function classFromText(L: string): ClassFilter | undefined {
  for (const [re, f] of CLASS_WORDS) if (re.test(L)) return f;
  return undefined;
}

/** Does a machine fall inside a class filter? */
export function inClass(machineId: string, f: ClassFilter | undefined): boolean {
  if (!f) return true;
  const m = FLEET.find((x) => x.machine_id === machineId);
  if (!m) return false;
  if (!f.classes.includes(m.cls)) return false;
  return !f.subclass || m.subclass === f.subclass;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/**
 * Component vocabulary. `word` is what the user says; `component` matches the
 * condition register / work-order component; `stock` is the part keyword;
 * `text` also matches work-order descriptions and root causes.
 *
 * The first four keep the regex engine's stock keywords (`coupling`, `brake`,
 * `front-end`, `tyre`) so a stock question that routed correctly before gets
 * the same parts back.
 */
const COMPONENT_WORDS: readonly (readonly [RegExp, Omit<ComponentRef, 'word'>])[] = [
  [/coupling/, { component: /hydraulic pump & drive/i, stock: 'coupling', text: /coupling/i }],
  [/brakes?\b|brake pads?|\bpads?\b|retarder/, { component: /brake|retarder/i, stock: 'brake', text: /brake|retarder/i }],
  [/front[- ]?end|rock drill/, { component: /rock drill/i, stock: 'front-end', text: /rock drill|front[- ]end/i }],
  [/tyres?\b/, { component: /tyres/i, stock: 'tyre', text: /tyre/i }],
  [/turbo(charger)?/, { component: /^engine$/i, stock: 'turbo', text: /turbo/i }],
  [/rotary head/, { component: /rotary head/i, stock: 'rotary head', text: /rotary head/i }],
  [/hammer|\bdth\b|drill string|drill pipes?/, { component: /dth hammer/i, stock: 'dth', text: /hammer|dth|string|pipe/i }],
  [/battery|\bbms\b/, { component: /battery/i, stock: 'battery', text: /battery/i }],
  [/charg(ing|er)|connector/, { component: /charging/i, stock: 'charging', text: /charg|connector/i }],
  [/\bhoses?\b/, { component: /hydraulic|boom/i, stock: 'hose', text: /hose/i }],
  [/transmission|clutch/, { component: /transmission/i, stock: 'transmission', text: /transmission|clutch/i }],
  [/cable reel|trailing cable/, { component: /cable reel/i, stock: 'cable', text: /cable/i }],
  [/carousel|rod changer/, { component: /carousel|rod changer/i, stock: 'carousel', text: /carousel|rod changer/i }],
  [/compressor/, { component: /compressor/i, stock: 'compressor', text: /compressor/i }],
  [/dust collector|\bdust\b/, { component: /dust/i, stock: 'dust', text: /dust/i }],
  [/tracks?\b|undercarriage/, { component: /tracks/i, stock: 'track', text: /track/i }],
  [/bucket|\bget\b/, { component: /bucket/i, stock: 'bucket', text: /bucket/i }],
  [/power pack/, { component: /power pack/i, stock: 'hydraulic', text: /power pack/i }],
  [/shank/, { component: /rock drill/i, stock: 'shank', text: /shank/i }],
  [/hydraulic pump|main pump/, { component: /hydraulic pump/i, stock: 'pump', text: /pump/i }],
  [/\bengines?\b/, { component: /engine/i, stock: 'engine', text: /engine/i }],
  [/suspension|strut/, { component: /suspension/i, stock: 'suspension', text: /suspension|strut/i }],
  [/fire suppression|\bfire\b/, { component: /fire suppression|engine/i, stock: 'fire', text: /fire/i }],
];

/** Every register component name, for exact mentions ("Rear axle & brakes"). */
const REGISTER_COMPONENTS = [...new Set(HEALTH.map((h) => h.component))];

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function componentsFromText(L: string): ComponentRef[] {
  const out: ComponentRef[] = [];
  for (const name of REGISTER_COMPONENTS) {
    if (name.length > 5 && L.includes(name.toLowerCase())) {
      out.push({
        word: name.toLowerCase(),
        component: new RegExp(`^${escape(name)}$`, 'i'),
        stock: name.split(/[ &(]/)[0].toLowerCase(),
        text: new RegExp(escape(name), 'i'),
      });
    }
  }
  for (const [re, ref] of COMPONENT_WORDS) {
    const m = re.exec(L);
    if (m && !out.some((o) => o.stock === ref.stock)) out.push({ word: m[0], ...ref });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Ids, amounts, levels, windows
// ---------------------------------------------------------------------------

const all = (q: string, re: RegExp): string[] =>
  [...new Set([...q.matchAll(re)].map((m) => m[0].toUpperCase()))];

/** `A$520`, `$1.2k`, `A$ 18,900`. */
export function amountFromText(q: string): number | undefined {
  const m = /\bA?\$\s?(\d[\d,]*(?:\.\d+)?)\s*(k|m)?\b/i.exec(q);
  if (!m) return undefined;
  const n = Number(m[1].replace(/,/g, ''));
  const mult = m[2]?.toLowerCase() === 'k' ? 1e3 : m[2]?.toLowerCase() === 'm' ? 1e6 : 1;
  return Number.isFinite(n) ? n * mult : undefined;
}

export function windowFromText(L: string): { days: number; label: string } | undefined {
  const n = /\b(?:next|in the next|within|coming)\s+(\d{1,3})\s+days?\b/.exec(L);
  if (n) return { days: Number(n[1]), label: `next ${n[1]} days` };
  const w = /\b(?:next|in the next|within)\s+(\d{1,2})\s+weeks?\b/.exec(L);
  if (w) return { days: Number(w[1]) * 7, label: `next ${w[1]} weeks` };
  if (/\btoday\b|this morning|this shift/.test(L)) return { days: 1, label: 'today' };
  if (/\btomorrow\b/.test(L)) return { days: 2, label: 'by tomorrow' };
  if (/overnight|last night/.test(L)) return { days: 1, label: 'overnight' };
  if (/this week|next week|coming week|\b7 days\b/.test(L)) return { days: 7, label: 'this week' };
  if (/fortnight|two weeks/.test(L)) return { days: 14, label: 'next 14 days' };
  if (/this month|next month|30 days/.test(L)) return { days: 30, label: 'next 30 days' };
  return undefined;
}

const MODULE_WORDS: readonly (readonly [ModuleId, RegExp])[] = MODULES.map((m) => [
  m.id,
  new RegExp(`\\b(${escape(m.name.toLowerCase())}|${escape(m.short.toLowerCase())})\\b`),
]);

// ---------------------------------------------------------------------------
// The extractor
// ---------------------------------------------------------------------------

/** Read every entity off one question. Pure. */
export function extractEntities(q: string): Entities {
  const L = q.toLowerCase();
  const machines = machinesFromText(q);
  const mines = minesFromText(q);
  const win = windowFromText(L);
  const lvl = /\bL([1-4])\b/.exec(q) ?? /\blevel ([1-4])\b/.exec(L);
  const mod = MODULE_WORDS.find(([, re]) => re.test(L));
  return {
    machines,
    machine: machines[0],
    machineFrom: machines[0] ? 'text' : undefined,
    mines,
    mine: mines[0],
    mineFrom: mines[0] ? 'text' : undefined,
    cls: classFromText(L),
    components: componentsFromText(L),
    partNos: all(q, /\bMER-[A-Z0-9-]+\b/gi),
    docIds: all(q, /\b(TB|SB|KB|SP|IR|PM-MER|LOTO|MI-SEIS|MR-BEV)-[A-Z0-9-]*\d[A-Z0-9-]*\b/gi).map((s) =>
      s.replace(/-+$/, ''),
    ),
    alertIds: all(q, /\bAL-\d{4}\b/gi),
    incidentIds: all(q, /\bIR-\d{4}-\d{3}\b/gi),
    woIds: all(q, /\bWO-\d{2}-\d{5}\b/gi),
    amount: amountFromText(q),
    level: lvl ? (Number(lvl[1]) as 1 | 2 | 3 | 4) : undefined,
    days: win?.days,
    windowLabel: win?.label,
    module: mod?.[0],
    moduleFrom: mod ? 'text' : undefined,
    deictic: {
      thisMachine: /this (machine|one|unit|rig|truck|loader|drill)|this page/.test(L),
      it: /\b(it|its|it's|that machine|that one|the same one)\b/.test(L),
      thisModule: /this module/.test(L),
      thisReport: /this report|the report/.test(L),
      thisSite: /this (site|mine)|\bhere\b/.test(L),
    },
  };
}

/** The alert on a machine, if one is open. */
export const alertFor = (machineId: string | undefined) =>
  machineId ? ALERTS.find((a) => a.machine === machineId) : undefined;

/** Every mine code, for "each mine" answers. */
export { MINE_ORDER };
